import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useRouteContext } from '@tanstack/react-router';
import { useState } from 'react';
import { EmptyState, ErrorState } from '@bb/ui';
import { ApiError, type AttributeStatus, type PiiClass } from '../api/client.ts';

export const ATTRIBUTE_STATUS_LABEL: Record<AttributeStatus, string> = { unreviewed: 'Unreviewed', active: 'Active', deprecated: 'Deprecated', blocked: 'Blocked' };
const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');
const shortDay = (iso: string | null) => (iso ? iso.slice(0, 10) : '—');

/** PII marker: a text badge with an icon, never colour alone. */
export function PiiBadge({ piiClass, review }: { piiClass: PiiClass; review?: boolean }) {
  if (piiClass === 'none') return <span className="hint">—</span>;
  return (
    <span className="tag tag--warning" title={review ? 'Classified by name; needs review' : 'Personal data'}>
      <span aria-hidden="true">🔒 </span>PII{review ? ' · needs review' : ''}
    </span>
  );
}

/** Data → Custom attributes (BB-26, BB-28): the registry with types, statuses, PII classes and the review backlog. */
export function AttributesPage() {
  const { environmentId } = useParams({ from: '/env/$environmentId/data/attributes' });
  const { api } = useRouteContext({ from: '/env/$environmentId/data/attributes' });
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<'' | AttributeStatus>('');
  const [piiClass, setPiiClass] = useState<'' | PiiClass>('');
  const [reviewRequired, setReviewRequired] = useState(false);
  const [tag, setTag] = useState('');
  const filters = { query, status, piiClass, reviewRequired, tag: tag.trim() };
  const attributes = useQuery({ queryKey: ['attributes', environmentId, filters], queryFn: () => api.listAttributes(environmentId, filters) });
  const filtered = Boolean(query || status || piiClass || reviewRequired || tag.trim());

  return (
    <section>
      <h1>Custom attributes</h1>
      {attributes.data && attributes.data.reviewRequired > 0 && (
        <p role="status" className="notice notice--warning">
          {attributes.data.reviewRequired} attributes were classified as PII by their name and need a review.
        </p>
      )}
      <div className="filters">
        <label>
          Search <input aria-label="Search attributes" value={query} onChange={(e) => setQuery(e.target.value)} />
        </label>
        <label>
          Status{' '}
          <select aria-label="Filter by status" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
            <option value="">All statuses</option>
            {Object.entries(ATTRIBUTE_STATUS_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          PII{' '}
          <select aria-label="Filter by PII class" value={piiClass} onChange={(e) => setPiiClass(e.target.value as typeof piiClass)}>
            <option value="">Any</option>
            <option value="pii">PII</option>
            <option value="none">Not PII</option>
          </select>
        </label>
        <label>
          Tag <input aria-label="Filter by tag" value={tag} onChange={(e) => setTag(e.target.value)} />
        </label>
        <label>
          <input type="checkbox" checked={reviewRequired} onChange={(e) => setReviewRequired(e.target.checked)} /> Needs review
        </label>
        <a className="button" href={`/admin/v1/environments/${environmentId}/catalog/attributes/export.csv`} download>
          Export CSV
        </a>
      </div>
      {attributes.isPending ? (
        <p aria-busy="true">Loading attributes…</p>
      ) : attributes.isError ? (
        <ErrorState title="Attributes could not be loaded" message={errorText(attributes.error)} onRetry={() => void attributes.refetch()} />
      ) : attributes.data.items.length === 0 ? (
        <EmptyState title="No attributes" description={filtered ? 'Nothing matches the filters.' : 'Attributes appear with the first write from the SDK or the backend.'} />
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Description</th>
              <th scope="col">Type</th>
              <th scope="col">PII</th>
              <th scope="col">Status</th>
              <th scope="col">Producer</th>
              <th scope="col">Last seen</th>
              <th scope="col">Conflicts 7d</th>
            </tr>
          </thead>
          <tbody>
            {attributes.data.items.map((a) => (
              <tr key={a.key}>
                <td>
                  <Link to="/env/$environmentId/data/attributes/$key" params={{ environmentId, key: a.key }}>
                    {a.key}
                  </Link>
                  {a.kind === 'standard' && <span className="hint"> standard</span>}
                </td>
                <td>{a.description ?? <span className="hint">—</span>}</td>
                <td>{a.dataType}</td>
                <td>
                  <PiiBadge piiClass={a.piiClass} review={a.piiReviewRequired} />
                </td>
                <td>
                  <span className={a.status === 'blocked' || a.status === 'unreviewed' ? 'tag tag--warning' : 'tag tag--neutral'}>{ATTRIBUTE_STATUS_LABEL[a.status]}</span>
                </td>
                <td>{a.producer}</td>
                <td>{shortDay(a.lastSeenAt)}</td>
                <td>{a.conflicts7d}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
